import classes from './FormContainer.module.css';
import { Title, Text, Alert } from '@mantine/core';
import type { TransactionResult } from './TransactionResult';
import { PaymentForm } from '@practist/components/PaymentForm/PaymentForm';
import type {
  OnSubmitPaymentProps,
  SuggestedAmountFromBackend,
} from '@practist/components/PaymentForm/paymentFormReducer';
import { submitPaymentFromClientToFirm } from '@/lib/cloudFunctions';
import { telemetry } from '@/lib/telemetry';
import type { PaymentMethodType } from '@shared/components/PaymentForm/PaymentMethodSelector';
import { useFinixEnvironment } from '@/app/FinixEnvironmentContext';
import { formatSuggestedAmountDescription } from './formatSuggestedAmountDescription';

export const FormContainer = ({
  firmId,
  matterId,
  firmName,
  finixMerchantId,
  initialTransactionAmount,
  paymentPageId,
  opacity,
  onTransactionEnded,
  colorPalette,
  availablePaymentMethods,
  matterName,
  attorneyName,
  practitionerTitleLabel,
  accountType,
  suggestedAmount,
}: {
  firmId: string;
  matterId: string;
  firmName: string;
  finixMerchantId: string;
  initialTransactionAmount: number | null;
  paymentPageId: string;
  opacity: number;
  colorPalette: {
    primary: string;
    secondary: string;
    tertiary?: string;
  };
  onTransactionEnded: (result: TransactionResult) => void;
  availablePaymentMethods: {
    ach: boolean;
    creditCard: boolean;
  };
  matterName: string;
  attorneyName: string | undefined;
  practitionerTitleLabel: string | undefined;
  accountType: 'trust' | 'operating';
  suggestedAmount: SuggestedAmountFromBackend | null;
}) => {
  const finixEnvironment = useFinixEnvironment();
  const suggestedAmountForDisplay =
    suggestedAmount === null
      ? null
      : {
          ...suggestedAmount,
          description: formatSuggestedAmountDescription(suggestedAmount),
        };

  const handleSubmitSucceeded = (transactionData: {
    payFacTransactionId: string;
    amount: number;
    paymentMethod: PaymentMethodType;
    paymentInstrument: {
      id: string;
      accountType: string | undefined;
      bankCode: string | undefined;
      lastFour: string | undefined;
      brand: string | undefined;
      cardType: string | undefined;
      expirationMonth: number | undefined;
      expirationYear: number | undefined;
      name: string | undefined;
      maskedAccountNumber: string | undefined;
    };
  }) => {
    onTransactionEnded({
      status: 'success',
      paymentMethod: transactionData.paymentMethod,
      amount: transactionData.amount.toString(),
      payFacTransactionId: transactionData.payFacTransactionId,
      paymentInstrument: transactionData.paymentInstrument,
    });
  };

  const isHidden = opacity === 0;

  const handleSubmitPayment = async (props: OnSubmitPaymentProps) => {
    const result = await submitPaymentFromClientToFirm({
      ...props,
      paymentPageId,
    });
    return result;
  };

  const finixApplicationId =
    finixEnvironment === 'sandbox'
      ? import.meta.env.VITE_FINIX_SANDBOX_APPLICATION_ID
      : import.meta.env.VITE_FINIX_LIVE_APPLICATION_ID;

  return (
    <div
      className={classes.formContainer}
      style={{
        opacity,
        visibility: isHidden ? 'hidden' : 'visible',
      }}
      aria-hidden={isHidden}
    >
      <div className={classes.formTitleWrapper}>
        <Alert
          w='100%'
          bg='white'
          radius='md'
          bd='1px solid var(--mantine-color-gray-2)'
        >
          <Title size='h3' order={2} className={classes.formTitle}>
            Matter: {matterName}
          </Title>
          {attorneyName && (
            <Text size='sm' c='dimmed'>
              {practitionerTitleLabel ?? 'Attorney'}: {attorneyName}
            </Text>
          )}
        </Alert>
      </div>

      <div
        style={{
          backgroundColor: 'var(--mantine-color-gray-0)',
          position: 'relative',
          width: '100%',
          margin: '0 auto',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <PaymentForm
          finixEnvironment={finixEnvironment}
          finixMerchantId={finixMerchantId}
          finixApplicationId={finixApplicationId}
          key={'payment-form'}
          firmId={firmId}
          matterId={matterId}
          firmName={firmName}
          initialTransactionAmount={initialTransactionAmount}
          paymentPageId={paymentPageId}
          visible={true}
          colorPalette={colorPalette}
          onSubmitSucceeded={handleSubmitSucceeded}
          availablePaymentMethods={availablePaymentMethods}
          onSubmitPayment={handleSubmitPayment}
          logger={telemetry}
          accountType={accountType}
          suggestedAmount={suggestedAmountForDisplay}
        />
      </div>
    </div>
  );
};
